import {SearchOutlined} from "@ant-design/icons";
import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {getWordpressUrl} from "@lib/wordpressVars";
import {Button, Col, Input, InputRef, Row} from "antd";
import {useWindowSize} from "@components/window-size-provider";
import BarcodeFileInput from "@components/barcode-read-provider/BarcodeFileInput";
import {useSearchBarcode} from "@components/barcode-read-provider/BarcodeReadProvider";
import {showInfoModal} from "@lib/showModal";
import {InfoIcon} from "@components/icons/Icons";
import useIsLoggedIn from "@hooks/useIsLoggedIn";

import comics_bg_img from "@images/topnav_bg_img.png";
import gradient_bg_img from "@images/search-background.png";

const TopnavSearchDropdownContent = ({searchBarcode, query, isDropDown, isLoggedIn, background}) => {
    const [searchValue, setSearchValue] = useState(query ? query : '');
    const windowDimensions = useWindowSize();

    const showDesktopView = useMemo(() => {
        return windowDimensions.over(799);
    }, [windowDimensions]);

    const bgImage = useMemo(() => {
        if(background === 'gradient') return gradient_bg_img;
        if(background === 'none') return '';
        return comics_bg_img;
    }, [background]);

    const onInfoClick = useCallback(() => {
        showInfoModal({
            title: "Find Comics On CovrPrice",
            content: (
                <>
                    <p><b>Search by Series</b> - Type in the series name then press Enter (e.g., Amazing Spider-Man)</p>
                    <p><b>Search by Issue</b> - Type in the series and issue number then press Enter (e.g., Amazing Spider-Man #300). You must include the hashtag (#)</p>
                    <p><b>Refine Your Search</b> - When searching by Issue, add additional descriptor text about the comic after the issue number (e.g., Amazing Spider-Man #300, Newsstand)</p>
                    <p><b>Search by Barcode</b> - (only available on mobile) Select the SCAN button, take a photo of the comic cover including the barcode (make sure the barcode is in focus and oriented right-side-up)</p>
                    <p>Happy Hunting!</p>
                </>
            ),
        });
    }, []);

    useEffect(() => {
        if(query !== searchValue) setSearchValue(query);
    }, [query]);

    const inputRef = useRef<InputRef>(null);
    const [isIntersecting, setIntersecting] = useState(false);
    const observer = useMemo(() => new IntersectionObserver(
        ([entry]) => setIntersecting(entry.isIntersecting)
    ), [inputRef]);

    useEffect(() => {
        if(inputRef && inputRef.current) {
            observer.observe((inputRef as any).current.input);
        }
        return () => observer.disconnect()
    }, [observer]);

    useEffect(() => {
        if(isDropDown && isIntersecting && inputRef && inputRef.current){
            setTimeout(() => inputRef!.current!.focus({cursor: 'all'}), 100);
        }
    }, [isIntersecting, inputRef]);

    return (
        <form action="/search/" style={{width: '100%'}}>
            <Row
                className={"topnav-search-dropdown flex-align-center flex-vertical-center " + ( bgImage === '' ? ' topnav-search-nobg ' : '')}
                style={bgImage === '' ? {} : {
                    background: `url(${getWordpressUrl(bgImage)}) 0 25% no-repeat`,
                    backgroundSize: `100.6% 100%`,
                }}
            >
                {background !== 'none' && (
                    <div className="search_info">
                        <div className={"search_info_title"}>Search comics by title and issue number</div>
                        <Button onClick={onInfoClick} type="link" className="text-white"
                                icon={<InfoIcon className={showDesktopView ? "iconLarge" : ""}/>}/>
                    </div>
                )}
                <Row className={"topnav-search-form "}>
                    <Col span={showDesktopView ? 20 : 19} className={"flex-col flex-align-center flex-vertical-center"}>
                        <Input
                            ref={inputRef}
                            name="search"
                            type="text"
                            placeholder="e.g. Amazing Spider-Man #300"
                            prefix={<SearchOutlined className={"iconGrey"}/>}
                            className="search_input"
                            value={searchValue}
                            onChange={(event) => {
                                setSearchValue(event.target.value)
                            }}
                        />
                    </Col>
                    <Col span={1} className={"flex-align-center flex-vertical-center"}>
                        <div className="vertical-line"/>
                    </Col>
                    <Col span={showDesktopView ? 3 : 4} className={"flex-vertical-center"}>
                        <BarcodeFileInput
                            isLoggedIn={isLoggedIn}
                            showDesktopView={showDesktopView}
                            toggleTopNavOnClick={isDropDown === true}
                            onChange={(barcode) => searchBarcode(barcode)}
                        />
                    </Col>
                </Row>
            </Row>
        </form>
    );
};

interface TopnavSearchDropdownProps {
    query?: string;
    background?: string;
    isOnPage?: boolean;
}

const TopnavSearchDropdown = ({query, background, isOnPage}: TopnavSearchDropdownProps) => {
    const searchBarcode = useSearchBarcode();
    const isLoggedIn = useIsLoggedIn();

    return (
        <TopnavSearchDropdownContent
            searchBarcode={searchBarcode}
            isDropDown={!isOnPage}
            query={query}
            isLoggedIn={isLoggedIn}
            background={background}
        />
    );
};

export default TopnavSearchDropdown;